/** @forge-header
 * @id          tickerHelpers
 * @role        Shared ticker parsing utilities | team lookups, player parsing, fallback
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,PlayerInfo,TickerCategory
 * @downstream  kalshiTicker/sports/* | kalshiTicker/parsers/*
 * @exports     labelToSport:fn | splitTimestampedCompetitors:fn | splitAnchoredCompetitors:fn | parseFallback:fn | parsePlayerFromMarket:fn | formatPlayerName:fn | formatTennisPlayerCode:fn | nflTeamFromCode:fn | nhlTeamFromCode:fn | mlbTeamFromCode:fn
 * @test        parse.test.ts (indirect)
 */

import { isCryptoTicker, isPoliticsTicker } from './nonSport';
import { MLB_TEAM_NAMES, NFL_TEAM_NAMES, NHL_TEAM_NAMES } from './teamNames';
import type { ParsedTicker, PlayerInfo, TickerCategory } from './types';

/** Parser function signature */
export type TickerParser = (raw: string) => ParsedTicker;

/**
 * Map parser label strings to sport keys for teamNameFromCode lookups.
 * NCAA labels return null (no lookup table for college teams).
 */
export function labelToSport(label: string): string | null {
  switch (label) {
    case 'NBA':
      return 'nba';
    case 'WNBA':
      return 'wnba';
    case 'NFL':
      return 'nfl';
    case 'NHL':
      return 'nhl';
    case 'MLB':
      return 'mlb';
    default:
      return null; // NCAA, etc. — no lookup table
  }
}

/** Default fallback for unrecognized tickers */
export function parseFallback(raw: string): ParsedTicker {
  const firstDash = raw.indexOf('-');
  const series = firstDash > 0 ? raw.slice(0, firstDash) : raw;

  let category: TickerCategory = 'unknown';
  if (series.startsWith('KXATTY') || series.startsWith('KXPRES') || isPoliticsTicker(raw)) {
    category = 'politics';
  } else if (isCryptoTicker(series)) {
    category = 'crypto';
  }

  return {
    raw,
    category,
    series,
    date: null,
    game: null,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome: null,
    shortLabel: raw,
    fullLabel: raw,
  };
}

/**
 * Parse player info from market part: SEAABARNER88 -> { teamCode: SEA, playerId: ABARNER, jersey: 88 }
 */
export function parsePlayerFromMarket(marketPart: string): PlayerInfo | null {
  if (marketPart.length < 4) return null;

  const teamCode = marketPart.slice(0, 3);
  const rest = marketPart.slice(3);

  const match = rest.match(/^([A-Z]+)(\d+)?$/);
  if (!match) {
    return {
      teamCode,
      playerId: rest,
      displayName: formatPlayerName(rest),
    };
  }

  const playerId = match[1] ?? rest;
  const jerseyNumber = match[2] ? Number(match[2]) : undefined;

  return {
    teamCode,
    playerId,
    jerseyNumber,
    displayName: formatPlayerName(playerId, jerseyNumber),
  };
}

/** Format player ID into display name: ABARNER -> A. Barner */
export function formatPlayerName(playerId: string, jersey?: number): string {
  if (!playerId) return '';

  const initial = playerId.charAt(0);
  const lastName = playerId.slice(1);
  const formatted = lastName.charAt(0).toUpperCase() + lastName.slice(1).toLowerCase();

  const name = `${initial}. ${formatted}`;
  return jersey !== undefined ? `${name} #${jersey}` : name;
}

/**
 * Title-case a competitor code lifted out of a ticker: `SINNER` → "Sinner",
 * `DJO` → "Djo".
 *
 * LAST RESORT ONLY. This is one line of casing — g1 has no player-name lookup
 * anywhere, so an abbreviated pack stays abbreviated ("Djo", "Conwel") and a
 * mis-split pack stays wrong ("Djos"). The real names ride on the live frame:
 * Kalshi puts the COMPLETE athlete name in each 1v1 market's `yes_sub_title`
 * (hydra's `subtitle`) and hydra's canonical fields carry full surnames. Use
 * those first (see `marketStore.competitorDisplayName` /
 * `describeMarket`); reach for this only when no frame is in hand.
 */
export function formatTennisPlayerCode(code: string): string {
  if (!code) return '';
  return code.charAt(0).toUpperCase() + code.slice(1).toLowerCase();
}

/** Strip an optional 4-digit HHMM start-time prefix that some sports embed
 *  between the date and the competitor codes (esports, cricket, MLB).
 *  "1700IMPSHIN" -> "IMPSHIN"; "FMAYMPAC" -> "FMAYMPAC".
 *
 *  The four digits must read as a real clock time (00-23 hours, 00-59
 *  minutes). That is the only evidence in the pack that separates a start
 *  time from a competitor code with leading digits, and without it the
 *  esports orgs whose names begin with digits ("1WIN", "100T", "4IKI",
 *  "9INE") cannot be told apart from the timestamp preceding them. An
 *  earlier form of this test also required a letter at position 5, which
 *  silently declined to strip exactly those packs and baked the start time
 *  into the team name ("13001WINFOK" -> codes "13001WIN"/"FOK"). Prefer
 *  `splitTimestampedCompetitors` when an outcome code is available — it can
 *  check the outcome anchor before accepting the strip. This remains a ticker
 *  fallback, not a substitute for canonical event identity. */
export function stripTimePrefix(s: string): string {
  // A pack still has to hold two competitors after the strip, so four digits
  // with nothing usable behind them are read as data, not as a timestamp.
  const looksTimestamped = s.length >= 6 && /^([01]\d|2[0-3])[0-5]\d/.test(s);
  return looksTimestamped ? s.slice(4) : s;
}

/** Strip Kalshi's MLB doubleheader suffix off a team-pair blob
 *  (`PITNYYG1` -> teams `PITNYY`, gameNumber 1). Team codes are pure
 *  letters, so a digit in the blob can only be this suffix. */
export function splitMlbDoubleheaderSuffix(teamBlob: string): {
  teams: string;
  gameNumber?: 1 | 2;
} {
  const m = teamBlob.match(/^([A-Z]+)G([12])$/);
  return m ? { teams: m[1], gameNumber: Number(m[2]) as 1 | 2 } : { teams: teamBlob };
}

/** A 3-way outcome code that represents the draw/tie leg (soccer). */
export function isDrawOutcome(code: string | null): boolean {
  return code === 'TIE' || code === 'DRAW';
}

/** Split a concatenated two-competitor code using the outcome code as the
 *  anchor — handles variable-length codes (e.g. "IMPSHIN" + outcome "IMP" ->
 *  ["IMP","SHIN"]). Falls back to the midpoint when the outcome doesn't bound
 *  either end. */
export function splitCompetitors(combined: string, outcome: string | null): [string, string] {
  if (outcome) {
    if (combined.startsWith(outcome)) return [outcome, combined.slice(outcome.length)];
    if (combined.endsWith(outcome)) {
      return [combined.slice(0, combined.length - outcome.length), outcome];
    }
  }
  const mid = Math.floor(combined.length / 2);
  return [combined.slice(0, mid), combined.slice(mid)];
}

/** Split a competitor pack that may carry an HHMM start-time prefix (esports,
 *  cricket, soccer), using the outcome code to reject a strip that would
 *  consume the named competitor.
 *
 *  A valid clock time is not proof on its own: "1000ACE" is a plausible 10:00
 *  start for team ACE and an equally plausible pairing of orgs 1000 and ACE.
 *  So both readings are tried, stripped first, and accepted only when the
 *  outcome anchors at an end with neither code left empty. Both readings can
 *  still anchor: stripped-first follows the venue's observed timestamped
 *  format, not a general proof of unique identity from the packed string.
 *  When neither reading anchors — a soccer draw leg, an unlabelled pack — the
 *  time is stripped if it reads as a clock time and the split falls through to
 *  `splitCompetitors`' midpoint guess, which remains a guess. */
export function splitTimestampedCompetitors(
  pack: string,
  outcome: string | null,
): [string, string] {
  const stripped = stripTimePrefix(pack);
  const readings = stripped === pack ? [pack] : [stripped, pack];

  for (const reading of readings) {
    const [first, second] = splitCompetitors(reading, outcome);
    if (!first || !second) continue;
    if (first === outcome || second === outcome) return [first, second];
  }
  return splitCompetitors(stripped, outcome);
}

/** Split a competitor pack the way `splitTimestampedCompetitors` does, but
 *  return null instead of guessing when nothing bounds the pack.
 *
 *  A packed pair carries no separator, so the trailing outcome code is the
 *  only evidence INSIDE the ticker for where the cut falls. A draw/tie leg
 *  names no competitor and a BTTS leg has no competitor outcome at all, so for
 *  those there is no evidence and there is no split: `KXMLSGAME-26AUG01MTLNE-TIE`
 *  midpointed to `MT`/`LNE` while its two sibling markets — which DO carry a
 *  team code — read `MTL`/`NE`, one event with two answers (#837).
 *
 *  Equal-length packs are refused for the same reason, not overlooked. `SEARSL`
 *  halves correctly and `SJLAFC` does not, and nothing in the string tells the
 *  two apart; a pair that happens to be right is still unproven. Callers
 *  recover the matchup from producer-owned canonical identity or render the
 *  ticker and the bet's meaning — they never mint a pair.
 *
 *  Draw codes are rejected here rather than at each call site so the two
 *  parsers that face this (soccer's explicit guard, esports' silent
 *  fallthrough) reach the same answer by the same route.
 *
 *  Same two-reading discipline as `splitTimestampedCompetitors`: the
 *  time-stripped reading first, then the raw one, so an org code that begins
 *  with digits (#838's `1WIN`, `100T`) is not mistaken for an HHMM start. */
// ── Soccer matchups learned from team tickets (2026-10-22) ────────────────────────────────────────────────────────
// A both-teams-to-score ticker (`KXUELBTTS-26OCT22BESCRY-BTTS`) names no team, so its pack `BESCRY` cannot be split
// without guessing (codes run 2–4 letters; #837 refuses a midpoint guess). Any TEAM ticker of the same match
// (`KXUELGAME-26OCT22BESCRY-BES`, a spread `…-CRY1`) proves the split, and the relay's game key does too, so each
// proven split is remembered per event segment (date + optional time + pack) and the BTTS leg reads it. Never a guess:
// an unseen match stays unresolved. Bounded; cleared wholesale when full.
const soccerPairs = new Map<string, [string, string]>();
const SOCCER_PAIRS_MAX = 5_000;
/** Remember a proven away/home split for an event segment (`26OCT22BESCRY`). */
export function noteSoccerPair(segment: string, away: string, home: string): void {
  const k = segment.toUpperCase();
  if (!k || !away || !home || soccerPairs.has(k)) return;
  if (soccerPairs.size >= SOCCER_PAIRS_MAX) soccerPairs.clear();
  soccerPairs.set(k, [away.toUpperCase(), home.toUpperCase()]);
}
/** The proven split for an event segment, or null. */
export function soccerPairOf(segment: string): [string, string] | null {
  return soccerPairs.get(segment.toUpperCase()) ?? null;
}
export function _clearSoccerPairsForTest(): void {
  soccerPairs.clear();
}

export function splitAnchoredCompetitors(
  pack: string,
  outcome: string | null,
): [string, string] | null {
  if (!outcome || isDrawOutcome(outcome)) return null;
  const stripped = stripTimePrefix(pack);
  for (const reading of stripped === pack ? [pack] : [stripped, pack]) {
    if (reading.length <= outcome.length) continue;
    if (reading.startsWith(outcome)) return [outcome, reading.slice(outcome.length)];
    if (reading.endsWith(outcome)) return [reading.slice(0, -outcome.length), outcome];
  }
  return null;
}

/** NFL team code to name mapping */
export function nflTeamFromCode(code: string): string | null {
  return NFL_TEAM_NAMES[code] ?? null;
}

/** MLB team code to name mapping (Kalshi 2-3 letter codes) */
export function mlbTeamFromCode(code: string): string | null {
  return MLB_TEAM_NAMES[code] ?? null;
}

/** NHL team code to name mapping */
export function nhlTeamFromCode(code: string): string | null {
  return NHL_TEAM_NAMES[code] ?? null;
}
